import isEmpty from 'lodash/isEmpty';
import { getBucketFromScore } from '@nerdwallet/nw-client-lib/lib/credit-score';
import * as selectors from './selectors';
var NOT_AVAILABLE_STRING = 'N/A';

var nullToNACheck = function nullToNACheck(value) {
  return value === undefined ? NOT_AVAILABLE_STRING : value;
};
/**
 * Converts an offer that is in the format returned from the Offer API into a format that matches
 * what several CC clients expect, which is more similar to the format returned by finproservices.
 * This also returns the string 'N/A' for many fields if they are otherwise undefined.
 *
 * @param {object} offer - the card object in the format returned by the Offer API
 * @param {object} payload - the payload associated with the offer. This is used to calculate
*                            average annual rewards since this knows the time period queried for
 * @returns {*} card - the new hybrid card format
 */


export var createCard = function createCard(offer, payload) {
  // eslint-disable-line import/prefer-default-export
  if (isEmpty(offer)) return null;
  var yearsWithCard = payload.getNumberOfMonths() / 12;
  return {
    // Keep original input params used for calculating rewards
    _originalPayloadJSON: payload.getJSON(),
    // Keep API response to make debugging easier.
    _originalShape: offer,
    // Translate to old api response for legacy code
    annual_fee: {
      details: selectors.getAnnualFeeDetails(offer),
      first_year: selectors.getAnnualFeeFirstYear(offer),
      value: selectors.getAnnualFee(offer)
    },
    apply: {
      link: selectors.getApplyLink(offer),
      pay_per_call: selectors.getPayPerCall(offer)
    },
    apr: {
      all: selectors.getAPRDisplayList(offer),
      balance_transfer: {
        fee: nullToNACheck(selectors.getBalanceTransferFee(offer)),
        intro: nullToNACheck(selectors.getBalanceTransferIntroAPR(offer)),
        intro_months: selectors.getBalanceTransferIntroTime(offer),
        zero_intro_months: nullToNACheck(selectors.getBalanceTransferIntroTime(offer)) // Note: zero_intro_months is a duplicate of intro_months

      },
      billing_cycle_unit: selectors.getBillingCycleUnits(offer),
      field_label: selectors.getIsFixedRate(offer) ? 'APR' : 'APR, Variable',
      intro_message: selectors.getAPRIntroMessageList(offer),
      ongoing: selectors.getOngoingAPR(offer),
      ongoing_annotation: selectors.getOngoingAPRAnnotated(offer),
      ongoing_unannotated: selectors.getOngoingAPRUnannotated(offer),
      purchase: {
        intro: nullToNACheck(selectors.getPurchaseIntroAPR(offer)),
        intro_months: nullToNACheck(selectors.getPurchaseIntroTime(offer)),
        zero_intro_months: nullToNACheck(selectors.getPurchaseIntroTime(offer)) // Note: zero_intro_months is a duplicate of intro_months

      },
      terms_and_fees_link: selectors.getTermsAndFeesLink(offer),
      min: selectors.getMinimumAPR(offer),
      max: selectors.getMaximumAPR(offer),

      /* This value is either boolean (normally), or null (if a compliance override is applied).
      We don't want to show the text "variable" if the card has a fixed rate, or an override is
      applied (SEE CCFE-908). */
      variable: selectors.getIsFixedRate(offer) === false ? 'Variable' : ''
    },
    calculated_rewards: {
      annual: Math.ceil(selectors.getNetRewards(offer) / yearsWithCard),
      fees: {
        annual: Math.ceil(selectors.getFees(offer) / yearsWithCard),
        total: selectors.getFees(offer)
      },
      sign_up_bonus: {
        annual: Math.ceil(selectors.getSignUpBonus(offer) / yearsWithCard),
        total: selectors.getSignUpBonus(offer)
      },
      spending: {
        annual: Math.ceil(selectors.getSpendingRewards(offer) / yearsWithCard),
        total: selectors.getSpendingRewards(offer)
      },
      time_with_card: yearsWithCard,
      total: selectors.getNetRewards(offer),
      true_balance_transfer_cost: nullToNACheck(selectors.getTrueBalanceTranferCost(offer)),
      true_interest_cost: selectors.getTrueInterestCost(offer)
    },
    card_details: {
      cons_bullets: selectors.getConsBullets(offer),
      marketing_bullets: selectors.getMarketingBullets(offer),
      pros_bullets: selectors.getProsBullets(offer)
    },
    card_image: {
      alt_text: selectors.getImageAltText(offer),
      is_portrait: selectors.getImageIsPortrait(offer),
      source: selectors.getImage(offer),
      source_large: selectors.getImage(offer, 'large'),
      source_medium: selectors.getImage(offer, 'medium')
    },
    card_name: selectors.getName(offer),
    card_user: selectors.getCardUser(offer),
    categories: selectors.getCategories(offer),
    disclaimer_text: selectors.getDisclaimer(offer),
    display_prequal_link: {
      cta: 'Get Pre-Qualified',
      // Should always be this string (from ccapi)
      link: selectors.getPrequalLink(offer),
      link_text: selectors.getPrequalLinkText(offer)
    },
    //
    id: selectors.getLegacyId(offer),
    is_obsolete: selectors.getStatus(offer) !== 'ACTIVE',
    // CHECK FOR ALL VALUES HERE
    issuer: selectors.getIssuerName(offer),
    max_fico: getBucketFromScore(selectors.getMaximumFICO(offer)),
    min_fico: getBucketFromScore(selectors.getMinimumFICO(offer)),
    obsolete_referral_card_ids: selectors.getObsoleteReferrals(offer),
    rewards: {
      card_type: selectors.getRewardsType(offer),
      rate: selectors.getRewardsRate(offer),
      rate_details: selectors.getRewardsRateDetails(offer),
      rate_notes: selectors.getRewardsRateNotes(offer),
      sign_up_bonus: selectors.getSignUpBonus(offer),
      sign_up_bonus_details: selectors.getSignUpBonusDetails(offer),
      sign_up_bonus_details_annotation: selectors.getSignUpBonusDetailsAnnotation(offer),
      categories: {
        dining: selectors.getCategorySpend(offer, 'dining'),
        gas: selectors.getCategorySpend(offer, 'gas'),
        groceries: selectors.getCategorySpend(offer, 'grocery_drug'),
        office: selectors.getCategorySpend(offer, 'office'),
        travel: selectors.getCategorySpend(offer, 'travel'),
        other: selectors.getCategorySpend(offer, 'other'),
        total: selectors.getNetRewards(offer),
        spending: selectors.getSpendingRewards(offer),
        bonus: selectors.getSignUpBonus(offer),
        annual_fee: selectors.getAnnualFee(offer),
        annual_fee_first_year: selectors.getAnnualFeeFirstYear(offer)
      }
    },
    slug: selectors.getSlug(offer),
    minimum_deposit: {
      value: selectors.getMinimumDeposit(offer),
      details: selectors.getMinimumDepositDescription(offer)
    },
    // Extra translated from marketplace api
    offerVersionId: selectors.getVersionId(offer)
  };
};